import { useState, useEffect } from "react";
import type { ReactNode } from "react";
import Badge from "../shared-components/Badge";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { classNames } from "../utils";
import ReportsCircleTooltip from "./helper-components/ReportsCircleTooltip";
import {
  ReportEntry,
  ReportEntryStyle,
  ReportRowItem,
} from "./helper-components/TableModels";
import { ReportSummaryItemApi } from "../api/Reports";

export type TableHeader =
  | string
  | { text: string | ReactNode; customClass: string };

interface TableProps {
  data: ReportRowItem<ReportSummaryItemApi>[];
  header: TableHeader[];
  isLoading: boolean;
}

const Table = ({ data, header, isLoading }: TableProps) => {
  const [tableData, setTableData] = useState<
    ReportRowItem<ReportSummaryItemApi>[]
  >(data !== undefined ? data : []);

  useEffect(() => {
    let table: ReportRowItem<ReportSummaryItemApi>[] = [];
    if (data) {
      table = [...data];
    }
    setTableData(table);
  }, [data]);

  return (
    <>
      <div>
        <div className="flex text-gray-500 rounded-t-xl border shadow shadow-gray-300">
          {header.map((header, idx) => (
            <div
              className={classNames(
                "flex rounded-t-xl   p-3 text-xs leading-4 font-medium bg-gray-50 uppercase",
                typeof header === "string" ? "flex-1" : header.customClass
              )}
              key={idx}
            >
              {typeof header === "string" ? header : header.text}
            </div>
          ))}
        </div>
        <div className="bg-white border-l border-r  rounded-b-xl shadow shadow-gray-300">
          {isLoading && <LoadingSpinner />}
          {!isLoading && tableData.length === 0 ? (
            <div>No search Items</div>
          ) : (
            tableData.map((report, i) => (
              <ReportRow reportRow={report} index={i} key={i} />
            ))
          )}
        </div>
      </div>
    </>
  );
};

interface ReportRowProps {
  reportRow: ReportRowItem<ReportSummaryItemApi>;
  index: number;
}

export const ReportRow = ({ reportRow, index }: ReportRowProps) => {
  const createEntry = (entry: ReportEntry, key: string) => {
    switch (entry.style) {
      case ReportEntryStyle.COMPONENT:
        return (
          <div
            key={key}
            className={classNames(
              "flex px-3 gap-2 items-center truncate",
              entry.customClass ? entry.customClass : "flex-1 w-4"
            )}
          >
            {entry.component}
          </div>
        );
      case ReportEntryStyle.BADGE:
        return (
          <div
            key={key}
            className="flex px-3 items-center flex-1 gap-2 truncate"
          >
            <Badge color={entry.badgeColor || "default"} hasDot={false}>
              {entry.text}
            </Badge>
          </div>
        );
      case ReportEntryStyle.BOLD_TEXT:
        return (
          <div
            key={key}
            className="flex px-3 items-center flex-1 font-semibold truncate"
          >
            {entry.text}
          </div>
        );
      case ReportEntryStyle.ROOT_COMPONENT:
        return <>{entry.component}</>;
      case ReportEntryStyle.TOOLTIP:
        return (
          <ReportsCircleTooltip reportRow={reportRow} key={key} flagKey={key} />
        );
      case ReportEntryStyle.TEXT:
      default:
        return (
          <div key={key} className="flex px-3 items-center flex-1 truncate">
            {entry.text}
          </div>
        );
    }
  };

  return (
    <div
      className={classNames(
        "flex border-b text-xs leading-4 font-normal py-4",
        reportRow.isLocked ? "bg-gray-100" : ""
      )}
      key={reportRow.report_uuid || `report-${index}`}
    >
      {reportRow.data.map((entry: ReportEntry, j: number) =>
        createEntry(entry, `report-${index}-entry-${j}`)
      )}
    </div>
  );
};

export default Table;
